'use client';

// Third party
import Postel from 'postel';

// Utils
import { useToggle } from '@hooks';
import { appendUrlParams } from '@utils/append-url-params';
import { ListingBySlugPartial } from '@utils/imperative-node-queries';

// UI
import { Icon, IconNames } from '@components/icon';
import { PhoneNumberModal } from './phone-number-modal';
import { ShareModal } from './share-modal';

type Props = {
  listing: ListingBySlugPartial;
  bookDirectWebsiteUrl?: string;
  publicEmail?: string;
  phoneNumber?: string;
  marketingWebsiteUrl?: string;
};

export const ContactLinks: React.FC<Props> = ({
  listing,
  bookDirectWebsiteUrl,
  publicEmail,
  phoneNumber,
  marketingWebsiteUrl,
}) => {
  const {
    on: phoneNumberModalIsOpen,
    setOff: closePhoneNumberModal,
    setOn: openPhoneNumberModal,
  } = useToggle();

  const {
    on: shareModalIsOpen,
    setOff: closeShareModal,
    setOn: openShareModal,
  } = useToggle();

  return (
    <>
      {phoneNumber && (
        <PhoneNumberModal
          isOpen={phoneNumberModalIsOpen}
          closeModal={closePhoneNumberModal}
          phoneNumber={phoneNumber}
        />
      )}

      <ShareModal
        isOpen={shareModalIsOpen}
        closeModal={closeShareModal}
        listing={listing}
      />

      <div className="flex flex-row items-center overflow-auto space-x-6 lg:justify-end">
        {marketingWebsiteUrl && (
          <Postel
            triggerDelay={0}
            hideDelay={0}
            title="View Webiste Tool Tip"
            placement="bottom"
            content={
              <div className="text-white text-xs font-semibold bg-black rounded-md shadow-lg opacity-80 px-4 py-3 mt-2">
                View Website
              </div>
            }
          >
            <a
              href={appendUrlParams(marketingWebsiteUrl, {
                utm_source: 'Savvy',
                utm_medium: 'website',
              })}
              target="_blank"
              className="relative"
              rel="noreferrer"
            >
              <Icon
                name={IconNames.BROWSER}
                size={'sm'}
                className="text-primary-main hover:text-primary-dark"
              />
            </a>
          </Postel>
        )}

        {bookDirectWebsiteUrl && (
          <Postel
            triggerDelay={0}
            hideDelay={0}
            title="Book Tool Tip"
            placement="bottom"
            content={
              <div className="text-white text-xs font-semibold bg-black rounded-md shadow-lg opacity-80 px-4 py-3 mt-2">
                Book
              </div>
            }
          >
            <a
              href={appendUrlParams(bookDirectWebsiteUrl, {
                utm_source: 'Savvy',
                utm_medium: 'website',
              })}
              target="_blank"
              rel="noreferrer"
            >
              <Icon
                name={IconNames.CALENDAR}
                size={'sm'}
                className="text-primary-main hover:text-primary-dark"
              />
            </a>
          </Postel>
        )}

        {phoneNumber && (
          <Postel
            triggerDelay={0}
            hideDelay={0}
            title="Call Tool Tip"
            placement="bottom"
            content={
              <div className="text-white text-xs font-semibold bg-black rounded-md shadow-lg opacity-80 px-4 py-3 mt-2 z-0">
                Call
              </div>
            }
          >
            <button
              className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main"
              onClick={openPhoneNumberModal}
            >
              <Icon
                name={IconNames.PHONE}
                size={'sm'}
                className="text-primary-main hover:text-primary-dark"
              />
            </button>
          </Postel>
        )}

        {publicEmail && (
          <Postel
            triggerDelay={0}
            hideDelay={0}
            title="Email Tool Tip"
            placement="bottom"
            content={
              <div className="text-white text-xs font-semibold bg-black rounded-md shadow-lg opacity-80 px-4 py-3 mt-2">
                Email
              </div>
            }
          >
            <a href={`mailto:${publicEmail}?subject=Inquiry from Savvy`}>
              <Icon
                name={IconNames.ENVELOPE}
                size={'sm'}
                className="text-primary-main hover:text-primary-dark"
              />
            </a>
          </Postel>
        )}

        <Postel
          triggerDelay={0}
          hideDelay={0}
          title="Share Tool Tip"
          placement="bottom"
          content={
            <div className="text-white text-xs font-semibold bg-black rounded-md shadow-lg opacity-80 px-4 py-3 mt-2">
              Share
            </div>
          }
        >
          <button
            className="outline-none focus-visible:ring-2 focus-visible:ring-primary-main"
            onClick={openShareModal}
          >
            <Icon
              name={IconNames.LINK}
              size={'sm'}
              className="text-primary-main hover:text-primary-dark"
            />
          </button>
        </Postel>
      </div>
    </>
  );
};
